import { AILayoutService } from "../../services/ai/AILayoutService";
import type { Component } from "../../types";
import { rebuildParentIds, validateHierarchy } from "../../utils/rebuildParentIds";
import { enhanceComponentsWithChildren } from "./editor-utils";
import { buildComponent, mergeIdMap, toIdMap } from "./editor-store-helpers";
import { syncCurrentPageState } from "./component-slice-helpers";
import type {
  ComponentSlice,
  ComponentSliceState,
} from "./component-slice-types";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";

function clampInsertIndex(index: number | undefined, length: number): number {
  if (index === undefined || !Number.isFinite(index)) {
    return length;
  }

  const normalizedIndex = Math.trunc(index);
  if (normalizedIndex <= 0) {
    return 0;
  }

  return Math.min(normalizedIndex, length);
}

function arraysEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function insertComponentIdAt(
  siblingIds: readonly string[],
  componentId: string,
  index: number | undefined,
): string[] {
  const siblingsWithoutComponent = siblingIds.filter(
    (id) => id !== componentId,
  );
  const insertIndex = clampInsertIndex(index, siblingsWithoutComponent.length);

  return [
    ...siblingsWithoutComponent.slice(0, insertIndex),
    componentId,
    ...siblingsWithoutComponent.slice(insertIndex),
  ];
}

function getRootSiblingIds(
  components: readonly Component[],
  pageId: string,
): string[] {
  return components
    .filter((component) => component.pageId === pageId && !component.parentId)
    .map((component) => component.id);
}

function isDescendantOf(
  componentId: string,
  ancestorId: string,
  componentMap: ReadonlyMap<string, Component>,
): boolean {
  let parentId = componentMap.get(componentId)?.parentId;
  const visitedParentIds = new Set<string>();

  while (parentId) {
    if (parentId === ancestorId) {
      return true;
    }

    if (visitedParentIds.has(parentId)) {
      return false;
    }
    visitedParentIds.add(parentId);

    parentId = componentMap.get(parentId)?.parentId;
  }

  return false;
}

function reorderRootComponents(
  components: readonly Component[],
  rootSiblingIds: readonly string[],
): Component[] {
  const componentMap = toIdMap(components);
  const orderedRootComponents = rootSiblingIds
    .map((id) => componentMap.get(id))
    .filter((component): component is Component => component !== undefined);
  const rootIds = new Set(rootSiblingIds);
  let replacementIndex = 0;

  return components.map((component) => {
    if (!rootIds.has(component.id)) {
      return component;
    }

    const replacement = orderedRootComponents[replacementIndex];
    replacementIndex += 1;

    return replacement ?? component;
  });
}

function addSiblingOrders(
  orderByComponentId: Map<string, number>,
  siblingIds: readonly string[] | undefined,
): void {
  siblingIds?.forEach((id, index) => {
    orderByComponentId.set(id, index);
  });
}

export function createComponentStructureActions<
  TState extends ComponentSliceState,
>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): Pick<
  ComponentSlice,
  | "applyResponsiveLayout"
  | "addComponentsWithResponsiveLayout"
  | "addChildToContainer"
  | "removeChildFromContainer"
  | "reorderChildrenInContainer"
  | "moveComponentBetweenContainers"
  | "moveComponentToParent"
  | "moveComponentBefore"
  | "moveComponentAfter"
  | "addComponentToPage"
  | "getPageComponents"
  | "getCurrentPageComponents"
  | "getComponent"
  | "getSelectedComponents"
  | "getComponentChildren"
  | "getComponentParent"
  | "getSubComponents"
  | "isComponentSelected"
  | "rebuildComponentHierarchy"
> {
  return {
    applyResponsiveLayout: async () => {
      const state = get();
      const aiLayoutService = AILayoutService.getInstance();

      try {
        const canvasWidth = state.canvasSettings.layout.maxWidth || 1200;
        const canvasHeight = state.canvasSettings.layout.maxHeight || 800;
        const layoutResult =
          await aiLayoutService.generateResponsiveLayoutAPI(
            state.components,
            canvasWidth,
            canvasHeight,
          );

        if (layoutResult.success && layoutResult.data) {
          const updatedComponents = aiLayoutService.applyResponsiveLayout(
            state.components,
            layoutResult.data,
          );

          set((draft) => {
            const enhancedComponents =
              enhanceComponentsWithChildren(updatedComponents);

            draft.components = enhancedComponents;
            draft.componentMap = toIdMap(enhancedComponents);
            draft.isModified = true;
            draft.lastUpdateTimestamp = Date.now();

            syncCurrentPageState(draft);
          });
        }
      } catch (error) {
        console.error("❌ Failed to apply responsive layout:", error);
      }
    },

    addComponentsWithResponsiveLayout: async (componentsData) => {
      const state = get();
      const newComponents = componentsData.map((componentData) =>
        buildComponent(
          componentData,
          state.currentPageId || "",
          state.project?.id,
        ),
      );

      set((draft) => {
        draft.components = [...draft.components, ...newComponents];
        draft.componentMap = mergeIdMap(draft.componentMap, newComponents);
        draft.isModified = true;
        draft.lastUpdateTimestamp = Date.now();

        if (draft.currentPageId) {
          const pageComponents =
            draft.pageComponents.get(draft.currentPageId) || [];
          const enhancedPageComponents = enhanceComponentsWithChildren([
            ...pageComponents,
            ...newComponents,
          ]);

          draft.pageComponents.set(draft.currentPageId, enhancedPageComponents);
          draft.pageHistoryMap.set(draft.currentPageId, draft.history);
        }
      });

      try {
        await get().applyResponsiveLayout();
      } catch (error) {
        console.error(
          "❌ Failed to apply responsive layout after adding components:",
          error,
        );
      }
    },

    addChildToContainer: (containerId, childComponentData, insertIndex) => {
      const childComponent = buildComponent(
        {
          ...childComponentData,
          parentId: containerId,
        },
        childComponentData.pageId ?? get().currentPageId ?? "",
        get().project?.id,
      );

      const container = get().getComponent(containerId);
      if (!container) {
        console.error("Container not found:", containerId);
        return;
      }

      get().addComponent(childComponent);

      if (insertIndex !== undefined && insertIndex >= 0) {
        const updatedContainer = get().getComponent(containerId);
        if (updatedContainer && updatedContainer.children.length > 1) {
          const children = [...updatedContainer.children];
          const childIndex = children.lastIndexOf(childComponent.id);
          if (childIndex !== -1 && childIndex !== insertIndex) {
            children.splice(childIndex, 1);
            const targetIndex = Math.min(insertIndex, children.length);
            children.splice(targetIndex, 0, childComponent.id);
            get().updateComponent(containerId, { children });
          }
        }
      }
    },

    removeChildFromContainer: (containerId, childId) => {
      const container = get().getComponent(containerId);
      if (!container) {
        console.error("Container not found:", containerId);
        return;
      }

      const updatedChildren = container.children.filter((id) => id !== childId);
      get().updateComponent(containerId, { children: updatedChildren });
      get().deleteComponent(childId);
    },

    reorderChildrenInContainer: (containerId, newOrder) => {
      const container = get().getComponent(containerId);
      if (!container) {
        console.error("Container not found:", containerId);
        return;
      }

      const validNewOrder = newOrder.filter((childId) =>
        container.children.includes(childId),
      );

      get().updateComponent(containerId, { children: validNewOrder });
    },

    moveComponentBetweenContainers: (
      componentId,
      fromContainerId,
      toContainerId,
    ) => {
      const component = get().getComponent(componentId);
      const fromContainer = get().getComponent(fromContainerId);
      const toContainer = get().getComponent(toContainerId);

      if (!component || !fromContainer || !toContainer) {
        console.error("Component or container not found:", {
          componentId,
          fromContainerId,
          toContainerId,
        });
        return;
      }

      const updatedFromChildren = fromContainer.children.filter(
        (id) => id !== componentId,
      );
      const updatedToChildren = [...toContainer.children, componentId];

      get().updateComponent(fromContainerId, { children: updatedFromChildren });
      get().updateComponent(toContainerId, { children: updatedToChildren });
      get().updateComponent(componentId, { parentId: toContainerId });
    },

    moveComponentToParent: (componentId, parentId, index) => {
      let didMove = false;

      set((state) => {
        const componentMap = toIdMap(state.components);
        const component = componentMap.get(componentId);
        const targetParent = parentId ? componentMap.get(parentId) : undefined;

        if (
          !component ||
          (parentId && !targetParent) ||
          parentId === componentId ||
          (parentId && isDescendantOf(parentId, componentId, componentMap)) ||
          (targetParent && targetParent.pageId !== component.pageId)
        ) {
          return;
        }

        const oldParentId = component.parentId;
        const oldParent = oldParentId
          ? componentMap.get(oldParentId)
          : undefined;
        const currentNewSiblingIds = parentId
          ? (targetParent?.children ?? [])
          : getRootSiblingIds(state.components, component.pageId);
        const nextNewSiblingIds = insertComponentIdAt(
          currentNewSiblingIds,
          componentId,
          index,
        );
        const nextOldSiblingIds =
          oldParentId && oldParentId !== parentId && oldParent
            ? oldParent.children.filter((id) => id !== componentId)
            : undefined;
        const nextRootSiblingIds =
          parentId === undefined
            ? nextNewSiblingIds
            : oldParentId === undefined
              ? getRootSiblingIds(state.components, component.pageId).filter(
                  (id) => id !== componentId,
                )
              : undefined;

        const parentChanged = oldParentId !== parentId;
        const newSiblingsChanged = !arraysEqual(
          currentNewSiblingIds,
          nextNewSiblingIds,
        );
        const oldSiblingsChanged =
          nextOldSiblingIds !== undefined &&
          oldParent !== undefined &&
          !arraysEqual(oldParent.children, nextOldSiblingIds);

        if (!parentChanged && !newSiblingsChanged && !oldSiblingsChanged) {
          return;
        }

        const childUpdatesByParentId = new Map<string, string[]>();
        if (oldParentId && nextOldSiblingIds) {
          childUpdatesByParentId.set(oldParentId, nextOldSiblingIds);
        }
        if (parentId) {
          childUpdatesByParentId.set(parentId, nextNewSiblingIds);
        }

        const orderByComponentId = new Map<string, number>();
        addSiblingOrders(orderByComponentId, nextRootSiblingIds);
        addSiblingOrders(orderByComponentId, nextOldSiblingIds);
        addSiblingOrders(
          orderByComponentId,
          parentId ? nextNewSiblingIds : undefined,
        );

        const updatedAt = new Date();
        let nextComponents = state.components.map((candidate) => {
          const nextChildren = childUpdatesByParentId.get(candidate.id);
          const nextOrder = orderByComponentId.get(candidate.id);
          const shouldUpdateParent = candidate.id === componentId;
          const shouldUpdateChildren = nextChildren !== undefined;
          const shouldUpdateOrder = nextOrder !== undefined;

          if (
            !shouldUpdateParent &&
            !shouldUpdateChildren &&
            !shouldUpdateOrder
          ) {
            return candidate;
          }

          return {
            ...candidate,
            parentId: shouldUpdateParent ? parentId : candidate.parentId,
            children: shouldUpdateChildren ? nextChildren : candidate.children,
            order: shouldUpdateOrder ? nextOrder : candidate.order,
            updatedAt,
          };
        });

        if (nextRootSiblingIds) {
          nextComponents = reorderRootComponents(
            nextComponents,
            nextRootSiblingIds,
          );
        }

        const enhancedComponents =
          enhanceComponentsWithChildren(nextComponents);
        state.components = enhancedComponents;
        state.componentMap = toIdMap(enhancedComponents);
        state.isModified = true;
        state.lastUpdateTimestamp = Date.now();

        syncCurrentPageState(state);
        didMove = true;
      });

      if (didMove) {
        get().pushToHistory();
      }
    },

    moveComponentBefore: (componentId, targetId) => {
      const state = get();
      const component = state.getComponent(componentId);
      const target = state.getComponent(targetId);

      if (!component || !target || component.pageId !== target.pageId) {
        return;
      }

      const targetParentId = target.parentId;
      const targetSiblings = targetParentId
        ? state.getComponent(targetParentId)?.children
        : getRootSiblingIds(state.components, target.pageId);
      if (!targetSiblings || !targetSiblings.includes(targetId)) {
        return;
      }

      const siblingsWithoutComponent = targetSiblings.filter(
        (id) => id !== componentId,
      );
      const targetIndex = siblingsWithoutComponent.indexOf(targetId);
      if (targetIndex === -1) {
        return;
      }

      state.moveComponentToParent(componentId, targetParentId, targetIndex);
    },

    moveComponentAfter: (componentId, targetId) => {
      const state = get();
      const component = state.getComponent(componentId);
      const target = state.getComponent(targetId);

      if (!component || !target || component.pageId !== target.pageId) {
        return;
      }

      const targetParentId = target.parentId;
      const targetSiblings = targetParentId
        ? state.getComponent(targetParentId)?.children
        : getRootSiblingIds(state.components, target.pageId);
      if (!targetSiblings || !targetSiblings.includes(targetId)) {
        return;
      }

      const siblingsWithoutComponent = targetSiblings.filter(
        (id) => id !== componentId,
      );
      const targetIndex = siblingsWithoutComponent.indexOf(targetId);
      if (targetIndex === -1) {
        return;
      }

      state.moveComponentToParent(componentId, targetParentId, targetIndex + 1);
    },

    addComponentToPage: (pageId, componentData) => {
      if (pageId === get().currentPageId) {
        get().addComponent({
          ...componentData,
          pageId,
        });
        return;
      }

      const component = buildComponent(
        {
          ...componentData,
          pageId,
        },
        pageId,
        get().project?.id,
      );

      set((state) => {
        const pageComponents = state.pageComponents.get(pageId) || [];
        const enhancedPageComponents = enhanceComponentsWithChildren([
          ...pageComponents,
          component,
        ]);

        state.pageComponents.set(pageId, enhancedPageComponents);
        state.isModified = true;
      });
    },

    getPageComponents: (pageId) => get().pageComponents.get(pageId) || [],

    getCurrentPageComponents: () => {
      const currentPageId = get().currentPageId;
      return currentPageId ? get().pageComponents.get(currentPageId) || [] : [];
    },

    getComponent: (id) => get().componentMap.get(id),

    getSelectedComponents: () => {
      const { selectedComponents, componentMap } = get();
      return selectedComponents
        .map((id) => componentMap.get(id))
        .filter((component): component is Component => component !== undefined);
    },

    getComponentChildren: (id) =>
      get().components.filter((component) => component.parentId === id),

    getComponentParent: (id) => {
      const component = get().getComponent(id);
      return component?.parentId
        ? get().getComponent(component.parentId)
        : undefined;
    },

    getSubComponents: (parentId) =>
      get().components.filter(
        (component) =>
          component.parentId === parentId &&
          component.isSubComponent === true,
      ),

    isComponentSelected: (id) => get().selectedComponents.includes(id),

    rebuildComponentHierarchy: () => {
      const rebuiltComponents = rebuildParentIds(get().components);
      validateHierarchy(rebuiltComponents);
      get().setComponents(rebuiltComponents);
    },
  };
}
